import { useEffect, useState } from "react";
import { IDashboardStats } from "../../models/DashboardStats";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import DashboardStatTableTabs from "../shared/Tabs/DashboardStatTableTabs";
import stringToColour from "../../utils/stringToColour";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const centerPlugin = {
  id: "center-label",
  beforeDraw: function (chart: any) {
    if (chart?.config?.options?.elements?.center) {
      const ctx = chart.ctx;

      const centerConfig = chart.config.options.elements.center;
      const fontStyle = centerConfig.fontStyle || "Arial";
      const txt = centerConfig.text;
      const color = centerConfig.color || "#000";
      const maxFontSize = centerConfig.maxFontSize || 75;
      const sidePadding = centerConfig.sidePadding || 20;
      const sidePaddingCalculated = (sidePadding / 100) * (chart.innerRadius * 2);

      ctx.font = "40px " + fontStyle;

      const stringWidth = ctx.measureText(txt).width;
      const elementWidth = chart.innerRadius * 2 - sidePaddingCalculated;

      const widthRatio = elementWidth / stringWidth;
      const newFontSize = Math.floor(30 * widthRatio);
      const elementHeight = chart.innerRadius * 2;

      let fontSizeToUse = Math.min(newFontSize, elementHeight, maxFontSize);
      let minFontSize = centerConfig.minFontSize;
      const lineHeight = centerConfig.lineHeight || 25;
      let wrapText = false;

      if (minFontSize === undefined) {
        minFontSize = 20;
      }

      if (minFontSize && fontSizeToUse < minFontSize) {
        fontSizeToUse = minFontSize;
        wrapText = true;
      }

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
      let centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
      ctx.font = fontSizeToUse + "px " + fontStyle;
      ctx.fillStyle = color;

      if (!wrapText) {
        ctx.fillText(txt, centerX, centerY);
        return;
      }

      const words = txt.split(" ");
      let line = "";
      const lines: string[] = [];

      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + " ";
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        if (testWidth > elementWidth && n > 0) {
          lines.push(line);
          line = words[n] + " ";
        } else {
          line = testLine;
        }
      }

      centerY -= (lines.length / 2) * lineHeight;

      for (let i = 0; i < lines.length; i++) {
        ctx.fillText(lines[i], centerX, centerY);
        centerY += lineHeight;
      }

      ctx.fillText(line, centerX, centerY);
    }
  },
};

ChartJS.register(centerPlugin);

const tabs = [
  { name: "Today", value: "Day" },
  { name: "This Week", value: "Week" },
  { name: "This Month", value: "Month" },
];

type Timeframe = "Day" | "Week" | "Month";

export default function SaleSourcePie({ dashboardStats }: { dashboardStats: IDashboardStats }) {
  const [timeframe, setTimeframe] = useState<Timeframe>("Day");
  const [stats, setStats] = useState<{ [key: string]: number } | undefined>(undefined);
  const [plugins, setPlugins] = useState<any[]>([]);

  useEffect(() => {
    if (!dashboardStats) return;

    let currentStats: { [key: string]: number } | undefined;

    switch (timeframe) {
      case "Day":
        currentStats = dashboardStats.salesSourceDay;
        break;
      case "Week":
        currentStats = dashboardStats.salesSourceWeek;
        break;
      case "Month":
        currentStats = dashboardStats.salesSourceMonth;
        break;
      default:
        currentStats = dashboardStats.salesSourceDay;
        break;
    }

    setStats(currentStats);
    setPlugins([centerPlugin]);
  }, [dashboardStats, timeframe]);

  if (!stats || Object.keys(stats).length === 0) {
    return <div></div>;
  }

  const labels = Object.keys(stats);
  const values = labels.map((label) => stats[label]);
  const total = values.reduce((sum, v) => sum + v, 0);

  const data = {
    labels,
    datasets: [
      {
        label: "Sales Source",
        data: values,
        backgroundColor: labels.map((label) => stringToColour(label)),
        hoverOffset: 4,
      },
    ],
  };

  const options: any = {
    responsive: true,
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10,
      },
    },
    cutoutPercentage: 90,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "left",
      },
      title: {
        display: false,
        text: "Sales Source",
      },
    },
    elements: {
      center: {
        text: total.toString(),
        color: "#035196",
        fontStyle: "Poppins",
        sidePadding: 20,
        minFontSize: 25,
        lineHeight: 25,
      },
    },
  };

  return (
    <div>
      <div className="mb-3 w-full flex justify-between">
        <div className="prose">
          <h3>Sales Source</h3>
        </div>

        <DashboardStatTableTabs
          tabs={tabs}
          value={timeframe}
          onChange={(val) => setTimeframe(val as Timeframe)}
          ariaLabel="Select timeframe"
        />
      </div>

      <div className="relative h-80">
        {/* @ts-ignore */}
        <Doughnut options={options} data={data} plugins={plugins || [centerPlugin]} />
      </div>
    </div>
  );
}
